/**
 * File: ChatInterface.tsx
 * Description: Main chat interface component using assistant-ui with backend integration
 * Author: System
 * Created: 2025-09-03
 * Last Modified: 2025-09-03
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ACTIVE_PROJECT_EVENT } from '../../utils/activeProject';

import { ChatRuntimeProvider, useChatHistory } from './ChatRuntime';
import { ChatThread } from './ChatThread';

interface ChatInterfaceProps {
  className?: string;
  onClearHistoryChange?: (clearHistory: (() => void) | undefined, isLoading: boolean) => void;
}

/**
 * Extract a valid project id from the current URL path or query string.
 */
const extractProjectIdFromUrl = (pathname: string, search: string): number | null => {
  // Try path first: /project/123
  const projectMatch = /\/project\/(\d+)/.exec(pathname);
  if (projectMatch) {
    return Number.parseInt(projectMatch[1], 10);
  }

  // Try query param: ?projectId=123
  const searchParams = new URLSearchParams(search);
  const projectIdParam = searchParams.get('projectId');
  if (projectIdParam) {
    return Number.parseInt(projectIdParam, 10);
  }

  return null;
};

/**
 * Internal component to expose chat history functions to parent
 */
const ChatHistoryExposer: React.FC<{ 
  onClearHistoryChange?: (clearHistory: (() => void) | undefined, isLoading: boolean) => void; 
}> = ({ 
  onClearHistoryChange,
}) => {
  const { clearHistory, isLoadingHistory } = useChatHistory();
  
  useEffect(() => {
    if (onClearHistoryChange) {
      onClearHistoryChange(clearHistory, isLoadingHistory || false);
    }
  }, [clearHistory, isLoadingHistory, onClearHistoryChange]);
  
  return null;
};

/**
 * ChatInterface - Main chat interface component with assistant-ui integration
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  className = '',
  onClearHistoryChange,
}) => {
  const location = useLocation();

  // Project ID comes from the URL when on a project page; on other pages it is
  // null until the user explicitly picks a project from the in-chat project
  // list. The previously used project is never preselected automatically.
  const [projectId, setProjectId] = React.useState<number | null>(
    () => extractProjectIdFromUrl(location.pathname, location.search),
  );

  useEffect(() => {
    // Follow the URL: use its project when present, otherwise clear selection.
    setProjectId(extractProjectIdFromUrl(location.pathname, location.search));
  }, [location.pathname, location.search]);

  useEffect(() => {
    // React when the active project is changed from within the chat (e.g. the
    // user picks a project from the in-chat project list on the front page).
    const handleActiveProjectChange = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      if (typeof detail === 'number' && !Number.isNaN(detail)) {
        setProjectId(detail);
      }
    };
    globalThis.addEventListener(ACTIVE_PROJECT_EVENT, handleActiveProjectChange);
    return () => {
      globalThis.removeEventListener(ACTIVE_PROJECT_EVENT, handleActiveProjectChange);
    };
  }, []);

  return (
    <div className={`flex flex-col h-full bg-base-100 ${className}`}>
      <div className="flex-1 min-h-0">
        <ChatRuntimeProvider key={projectId ?? 'no-project'} projectId={projectId}>
          <ChatHistoryExposer onClearHistoryChange={onClearHistoryChange} />
          <ChatThread projectId={projectId} />
        </ChatRuntimeProvider>
      </div>
    </div>
  );
};
